'use client';
import { jsx } from 'react/jsx-runtime';
import { Slot } from '@radix-ui/react-slot';
import { forwardRef, useContext, version } from 'react';
import { Button } from '../button/button.js';
import { Context } from './popover-trigger-context.js';

/**
 * PopoverTrigger component, used to trigger a popover.
 *
 * @example
 * <PopoverTriggerContext>
 *   <PopoverTrigger>Open Popover</PopoverTrigger>
 *   <Popover>
 *     Content
 *   </Popover>
 * </PopoverTriggerContext>
 *
 * @example inline
 * <PopoverTriggerContext>
 *   <Paragraph>
 *    We can use it <PopoverTrigger inline={true}>inline</PopoverTrigger>.
 *   </Paragraph>
 *   <Popover>
 *     Content
 *   </Popover>
 * </PopoverTriggerContext>
 */
const PopoverTrigger = forwardRef(function PopoverTrigger({ id, inline, asChild, ...rest }, ref) {
    const { popoverId } = useContext(Context);
    const Component = asChild ? Slot : inline ? 'button' : Button;
    const popoverProps = Object.assign({
        [version.startsWith('19') ? 'popoverTarget' : 'popovertarget']: popoverId,
        ...(inline
            ? {
                'data-popover': 'inline',
            }
            : {}),
    }, rest);
    return jsx(Component, { ref: ref, ...popoverProps });
});

export { PopoverTrigger };
